import type { SensitiveApprovalResponse, SensitiveApprovalWebsocketPayload } from '@/features/sensitive-approval/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  buildApprovalBusinessKey,
  formatApprovalPendingTarget,
  isApprovalBlockingStatus,
  isApprovalTerminalStatus,
  normalizeApprovalPendingTicket,
  type SensitiveApprovalBusinessTarget,
  type SensitiveApprovalPendingTicket,
} from '@/features/sensitive-approval/approvalPending'
import store from '@/store'

export const useSensitiveApprovalStore = defineStore('sensitiveApproval', () => {
  const ticketsById = ref<Record<number, SensitiveApprovalPendingTicket>>({})
  const ticketsByKey = ref<Record<string, SensitiveApprovalPendingTicket>>({})
  const floatingTicketIds = ref<Record<number, true>>({})
  const latestTicketId = ref<number>()
  const requestedReviewTicketId = ref<number>()

  const activeTickets = computed(() => {
    return Object.values(ticketsById.value)
      .filter(ticket => isApprovalBlockingStatus(ticket.status))
      .sort((left, right) => right.updatedAt - left.updatedAt)
  })

  const activeCount = computed(() => activeTickets.value.length)
  const floatingTickets = computed(() => {
    return activeTickets.value.filter(ticket => Boolean(ticket.ticketId && floatingTicketIds.value[ticket.ticketId]))
  })
  const floatingCount = computed(() => floatingTickets.value.length)
  const latestTicket = computed(() => {
    if (latestTicketId.value) {
      const ticket = ticketsById.value[latestTicketId.value]
      if (ticket && isApprovalBlockingStatus(ticket.status)) {
        return ticket
      }
    }
    return activeTickets.value[0]
  })
  const latestFloatingTicket = computed(() => floatingTickets.value[0])

  function removeTicket(ticket: SensitiveApprovalPendingTicket) {
    if (ticket.ticketId) {
      delete ticketsById.value[ticket.ticketId]
      delete floatingTicketIds.value[ticket.ticketId]
    }
    if (ticket.key) {
      delete ticketsByKey.value[ticket.key]
    }
    if (latestTicketId.value === ticket.ticketId) {
      latestTicketId.value = activeTickets.value[0]?.ticketId
    }
    if (requestedReviewTicketId.value === ticket.ticketId) {
      requestedReviewTicketId.value = undefined
    }
  }

  function upsertFromPayload(payload: SensitiveApprovalResponse | SensitiveApprovalWebsocketPayload) {
    const previous = payload.ticketId ? ticketsById.value[payload.ticketId] : undefined
    const now = Date.now()
    const ticket = normalizeApprovalPendingTicket(payload, now)
    if (!ticket) {
      return null
    }

    const nextTicket = {
      ...previous,
      ...ticket,
      key: ticket.key ?? previous?.key,
      createdAt: previous?.createdAt ?? ticket.createdAt,
      updatedAt: now,
    }

    if (isApprovalTerminalStatus(nextTicket.status)) {
      removeTicket(nextTicket)
      return nextTicket
    }

    if (nextTicket.ticketId) {
      ticketsById.value[nextTicket.ticketId] = nextTicket
      latestTicketId.value = nextTicket.ticketId
    }
    if (nextTicket.key) {
      ticketsByKey.value[nextTicket.key] = nextTicket
    }
    return nextTicket
  }

  function getBlockingTicket(target: SensitiveApprovalBusinessTarget) {
    const key = buildApprovalBusinessKey(target)
    if (!key) {
      return undefined
    }
    const ticket = ticketsByKey.value[key]
    return ticket && isApprovalBlockingStatus(ticket.status) ? ticket : undefined
  }

  function isBusinessBlocked(target: SensitiveApprovalBusinessTarget) {
    return Boolean(getBlockingTicket(target))
  }

  function getBlockingText(target: SensitiveApprovalBusinessTarget) {
    const ticket = getBlockingTicket(target)
    if (!ticket) {
      return ''
    }
    return `${formatApprovalPendingTarget(ticket)}正在审核中，请等待审核结果`
  }

  function showFloatingTicket(ticketId?: number) {
    if (!ticketId) {
      return
    }
    const ticket = ticketsById.value[ticketId]
    if (!ticket || !isApprovalBlockingStatus(ticket.status)) {
      return
    }
    floatingTicketIds.value[ticketId] = true
  }

  function hideFloatingTicket(ticketId?: number) {
    if (!ticketId) {
      return
    }
    delete floatingTicketIds.value[ticketId]
  }

  function requestReview(ticketId?: number) {
    if (!ticketId) {
      return
    }
    requestedReviewTicketId.value = ticketId
  }

  function consumeReviewRequest(ticketId?: number) {
    if (ticketId && requestedReviewTicketId.value !== ticketId) {
      return
    }
    requestedReviewTicketId.value = undefined
  }

  function clear() {
    ticketsById.value = {}
    ticketsByKey.value = {}
    floatingTicketIds.value = {}
    latestTicketId.value = undefined
    requestedReviewTicketId.value = undefined
  }

  return {
    ticketsById,
    ticketsByKey,
    floatingTicketIds,
    requestedReviewTicketId,
    activeTickets,
    activeCount,
    floatingTickets,
    floatingCount,
    latestTicket,
    latestFloatingTicket,
    upsertFromPayload,
    getBlockingTicket,
    getBlockingText,
    isBusinessBlocked,
    showFloatingTicket,
    hideFloatingTicket,
    requestReview,
    consumeReviewRequest,
    clear,
  }
})

export function useSensitiveApprovalStoreHook() {
  return useSensitiveApprovalStore(store)
}
